import { escapeHtml } from '../html/escape';
import { resolveSiteIconSize, type SiteIconSizeToken } from './iconSizes';
import type { LucideSvgIconNode } from './lucideSvgIconNode';
import {
  resolveSiteSocialIcon,
  type SimpleIcon,
  type SiteSocialNetwork,
} from './socialNetworks';

export interface RenderIconHtmlOptions {
  size?: number | SiteIconSizeToken;
  className?: string;
  /** kebab-case Lucide name for `lucide-${name}` class parity with React icons. */
  name?: string;
}

const LUCIDE_STROKE_WIDTH = 1.5;

function renderIconNodeChildren(iconNode: LucideSvgIconNode): string {
  return iconNode
    .map(([tag, attrs]) => {
      const attrHtml = Object.entries(attrs)
        .filter(([key]) => key !== 'key')
        .map(([key, value]) => `${key}="${escapeHtml(String(value))}"`)
        .join(' ');
      return `<${tag}${attrHtml ? ` ${attrHtml}` : ''}></${tag}>`;
    })
    .join('');
}

/**
 * SSR / edge string markup for Lucide site-template icons (parity with `LucideIcon`).
 * Builds SVG from icon geometry — does not invoke lucide-react client Icon components.
 */
export function renderLucideIconHtml(
  iconNode: LucideSvgIconNode,
  options: RenderIconHtmlOptions & { strokeWidth?: number } = {},
): string {
  const px = resolveSiteIconSize(options.size ?? 'md');
  const strokeWidth = options.strokeWidth ?? LUCIDE_STROKE_WIDTH;
  const className = [
    'lucide',
    options.name ? `lucide-${options.name}` : null,
    'ws-icon',
    options.className,
  ]
    .filter(Boolean)
    .join(' ');
  const children = renderIconNodeChildren(iconNode);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" class="${escapeHtml(className)}" aria-hidden="true">${children}</svg>`;
}

/** SSR / edge string markup for social marks (parity with `SocialIcon`). */
export function renderSocialIconHtml(
  icon: SiteSocialNetwork | SimpleIcon,
  options: RenderIconHtmlOptions = {},
): string {
  const brand = resolveSiteSocialIcon(icon);
  const px = resolveSiteIconSize(options.size ?? 'md');
  const className = ['ws-social-icon', options.className].filter(Boolean).join(' ');
  const classAttr = className ? ` class="${escapeHtml(className)}"` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${px}" height="${px}" fill="currentColor"${classAttr} aria-hidden="true"><path d="${escapeHtml(brand.path)}"></path></svg>`;
}
